import type { MerchantMallOrderRecord } from '@redd/contracts';

export type MerchantProfileRange = { from: string; to: string; granularity: 'day' | 'month' };
export type MerchantMallFilters = { page: number; q: string; status: '' | MerchantMallOrderRecord['status']; paymentMethod: '' | MerchantMallOrderRecord['paymentMethod'] };
export const initialMerchantMallFilters: MerchantMallFilters = { page: 1, q: '', status: '', paymentMethod: '' };

/** Inputs are inclusive China calendar dates; both APIs use an exclusive end. */
export function merchantProfilePeriod(range: MerchantProfileRange) {
  const parse = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('请选择正确的开始和结束日期');
    const utc = Date.parse(`${value}T00:00:00Z`);
    if (!Number.isFinite(utc) || new Date(utc).toISOString().slice(0, 10) !== value) throw new Error('请选择正确的开始和结束日期');
    return utc - 8 * 3_600_000;
  };
  const from = parse(range.from), to = parse(range.to);
  if (from > to) throw new Error('开始日期不能晚于结束日期');
  return { from: new Date(from).toISOString(), to: new Date(to + 86_400_000).toISOString(), granularity: range.granularity };
}

export function merchantMallDetailPath(merchantId: string, range: MerchantProfileRange, filters: MerchantMallFilters) {
  const query = new URLSearchParams({ ...merchantProfilePeriod(range), page: String(filters.page), pageSize: '25' });
  if (filters.q.trim()) query.set('q', filters.q.trim());
  if (filters.status) query.set('status', filters.status);
  if (filters.paymentMethod) query.set('paymentMethod', filters.paymentMethod);
  return `/v1/admin/merchants/${encodeURIComponent(merchantId)}/mall?${query}`;
}

export function updateMerchantMallFilters(current: MerchantMallFilters, change: Partial<Omit<MerchantMallFilters, 'page'>>) {
  return { ...current, ...change, page: 1 };
}

export type MerchantMallLoadState<T> = { key: string; requestId: number; loading: boolean; data?: T; error?: string };
export type MerchantMallLoadAction<T> = { type: 'start'; key: string; requestId: number } | { type: 'success'; requestId: number; data: T } | { type: 'failure'; requestId: number; error: string };
/** A slow earlier request must not replace the current merchant, filters, or retry. */
export function merchantMallLoadReducer<T>(state: MerchantMallLoadState<T>, action: MerchantMallLoadAction<T>): MerchantMallLoadState<T> {
  if (action.type === 'start') return { key: action.key, requestId: action.requestId, loading: true };
  if (state.requestId !== action.requestId) return state;
  if (action.type === 'success') return { key: state.key, requestId: state.requestId, loading: false, data: action.data };
  return { key: state.key, requestId: state.requestId, loading: false, error: action.error };
}

export const merchantMallStatusLabels: Record<MerchantMallOrderRecord['status'], string> = { pending: '待支付', paid: '已支付', refunded: '已退款', closed: '已关闭' };
export const merchantMallVoucherLabels: Record<NonNullable<MerchantMallOrderRecord['voucherStatus']>, string> = { UNUSED: '待提货', PROCESSING: '提货处理中', REDEEMED: '已提货', REFUNDED: '已退款作废' };
export const merchantMallDeliveryLabels: Record<NonNullable<MerchantMallOrderRecord['deliveryStatus']>, string> = { queued: '待发货', sending: '发货中', sent: '已发送报价', uncertain: '结果待确认', delivered: '已交付', review: '待人工核对', cancelled: '交付已取消' };
